import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import API_BASE_URL from "../api/config";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));

  const isAuthenticated = !!token;

  // Validate token on every page load
  useEffect(() => {
    if (!token) return;

    fetch(`${API_BASE_URL}/auth/validate`, {
      method: "GET",
      headers: { "Authorization": `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) {
          // Token is expired or invalid — log out
          localStorage.removeItem("authToken");
          setToken(null);
        }
      })
      .catch(() => {
        // Network error — don't log out (could be temporary)
      });
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || "Login failed");
    }

    const data = await res.json();
    localStorage.setItem("authToken", data.token);
    setToken(data.token);
    return data.token;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("authToken");
    setToken(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

